import type { User } from "firebase/auth";
import { isNativeBanculem, nativeSession } from "./native-app";

export const ALLOWED_EMAIL = "fernando.aguilarcar@gmail.com";
const AUTH_HINT_KEY = "banculem:auth-ok:v1";

async function loadAuth() {
  const [sdk, { firebaseApp }] = await Promise.all([import("firebase/auth"), import("./firebase")]);
  // The companion handles Google itself. A browser redirect resolver would
  // eagerly load a cross-origin iframe on iPhone before authStateReady settles.
  const auth = isNativeBanculem()
    ? sdk.initializeAuth(firebaseApp, {
      persistence: [sdk.indexedDBLocalPersistence, sdk.browserLocalPersistence, sdk.browserSessionPersistence],
    })
    : sdk.getAuth(firebaseApp);
  return { ...sdk, auth };
}

export function hasTrustedSessionHint(): boolean {
  // La sesión nativa debe comprobarse antes de mostrar una copia local.
  if (isNativeBanculem()) return false;
  return localStorage.getItem(AUTH_HINT_KEY) === ALLOWED_EMAIL;
}

export async function observeBanculemUser(onChange: (user: User | null) => void): Promise<() => void> {
  const { auth, onAuthStateChanged, signInWithCredential, GoogleAuthProvider, signOut } = await loadAuth();
  if (isNativeBanculem()) {
    await auth.authStateReady();
    try {
      const session = await nativeSession(false);
      const existing = auth.currentUser;
      const sameOwner = Boolean(session.uid && existing?.uid === session.uid && existing.email === ALLOWED_EMAIL && existing.emailVerified);
      // Native identity is checked first. Firebase already maintains this verified
      // web session; exchanging Google credentials again would block every launch.
      if (session.idToken && !sameOwner) {
        const result = await signInWithCredential(auth, GoogleAuthProvider.credential(session.idToken));
        if (result.user.uid !== session.uid || result.user.email !== ALLOWED_EMAIL || !result.user.emailVerified) throw new Error("Sesión no autorizada.");
      } else if (!session.uid || auth.currentUser?.uid !== session.uid) {
        await signOut(auth);
      }
    } catch { await signOut(auth); }
  }
  return onAuthStateChanged(auth, (user) => {
    const allowed = user?.email === ALLOWED_EMAIL && user.emailVerified;
    if (allowed) localStorage.setItem(AUTH_HINT_KEY, ALLOWED_EMAIL);
    else localStorage.removeItem(AUTH_HINT_KEY);
    onChange(allowed ? user : null);
  });
}

export async function signInToBanculem(): Promise<User> {
  const { auth, GoogleAuthProvider, signInWithPopup, signInWithCredential, signOut } = await loadAuth();
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ login_hint: ALLOWED_EMAIL });
  const session = isNativeBanculem() ? await nativeSession(true) : undefined;
  if (session && !session.idToken) throw new Error("Vuelve a conectar tu cuenta de Google.");
  const result = session
    ? await signInWithCredential(auth, GoogleAuthProvider.credential(session.idToken!))
    : await signInWithPopup(auth, provider);
  if (result.user.email !== ALLOWED_EMAIL || !result.user.emailVerified || (session && result.user.uid !== session.uid)) {
    await signOut(auth);
    throw new Error("Esta cuenta no tiene acceso a Banculem.");
  }
  localStorage.setItem(AUTH_HINT_KEY, ALLOWED_EMAIL);
  return result.user;
}
